/**
 * Google and Facebook sign-in (Milestone 4, D21, D28), next to the email code (account.ts). Both
 * leave the game for the provider's page and come back to /auth/<provider>, where the game picks
 * up again: a full-page redirect works on phones and in installed apps, where pop-ups don't.
 *
 * - Google: our own OpenID Connect request for an ID token, so Google's page says "continue to
 *   caged.gg" (Supabase's redirect would show its own address). The token goes to Supabase.
 * - Facebook: Supabase's OAuth redirect (Facebook's page shows the app's name).
 *
 * A guest's account gets the identity linked (same user, so progress stays, like registering
 * with an email). When the identity already belongs to an account, the device signs in to that
 * one instead, like logging in. The online code (Supabase) loads only when needed.
 */
import { SUPABASE_KEY, SUPABASE_URL } from './config';

export type SocialProvider = 'google' | 'facebook';
export type SocialMode = 'register' | 'login';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
const PENDING_KEY = 'caged.socialSignIn.v1';
/** A sign-in that hasn't come back by then is forgotten. */
const PENDING_MS = 15 * 60_000;

/** Sign-in or linking failed (the sheet says to try again, or use the email). */
export class SocialFailure extends Error {
  readonly problem = 'socialFailed';
}

/** What was started, kept while the player is on the provider's page. */
interface Pending {
  provider: SocialProvider;
  mode: SocialMode;
  /** Google: the nonce (Google gets its SHA-256) and the state it hands back. */
  nonce?: string;
  state?: string;
  /** Facebook: linking a guest's account (else signing in). */
  link?: boolean;
  at: number;
}

/** What came back from the provider, read once at start (readSocialReturn). */
export interface SocialReturn {
  provider: SocialProvider;
  mode: SocialMode;
  /** Google's ID token and the nonce it was asked for with. */
  idToken?: string;
  nonce?: string;
  /** Facebook: the session Supabase handed back, and whether it linked the guest. */
  session?: { access_token: string; refresh_token: string };
  link?: boolean;
  /** Facebook: the identity belongs to another account (sign in to that one). */
  identityTaken?: boolean;
  /** The player backed out on the provider's page. */
  cancelled?: boolean;
  failed?: boolean;
}

/** 'redirecting': Facebook goes round once more to sign in to the account it belongs to. */
export type SocialOutcome = 'linked' | 'signedIn' | 'redirecting' | 'cancelled';

function save(p: Pending): void {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(p));
  } catch {
    // Without storage the answer can't be matched; the sign-in then simply fails.
  }
}

function take(): Pending | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    localStorage.removeItem(PENDING_KEY);
    const p = raw ? (JSON.parse(raw) as Pending) : null;
    return p && Date.now() - p.at < PENDING_MS ? p : null;
  } catch {
    return null;
  }
}

function randomHex(bytes: number): string {
  return Array.from(crypto.getRandomValues(new Uint8Array(bytes)), (b) => b.toString(16).padStart(2, '0')).join('');
}

async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

const back = (provider: SocialProvider) => `${location.origin}/auth/${provider}`;

/**
 * iPhone home-screen apps open other sites in a separate browser sheet, so the answer wouldn't
 * come back to the app: they use the email code.
 */
const installedOnIos = () => (navigator as Navigator & { standalone?: boolean }).standalone === true;

let available: Promise<Record<SocialProvider, boolean>> | null = null;

/** The providers both this build and the Supabase project have switched on. */
export function socialProviders(): Promise<Record<SocialProvider, boolean>> {
  available ??= (async () => {
    const none = { google: false, facebook: false };
    if (!SUPABASE_URL || !SUPABASE_KEY || installedOnIos()) return none;
    try {
      const res = await fetch(`${SUPABASE_URL}/auth/v1/settings`, { headers: { apikey: SUPABASE_KEY }, signal: AbortSignal.timeout(6000) });
      const on = ((await res.json()) as { external?: Record<string, boolean> }).external ?? {};
      return { google: !!GOOGLE_CLIENT_ID && on.google === true, facebook: on.facebook === true };
    } catch {
      available = null;
      return none;
    }
  })();
  return available;
}

/** Leaves for the provider's page. */
export async function startSocial(provider: SocialProvider, mode: SocialMode): Promise<void> {
  if (provider === 'google') {
    if (!GOOGLE_CLIENT_ID) throw new SocialFailure('google is not set up');
    const nonce = randomHex(32);
    const state = randomHex(16);
    const url = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    url.search = new URLSearchParams({
      client_id: GOOGLE_CLIENT_ID,
      redirect_uri: back(provider),
      response_type: 'id_token',
      scope: 'openid email',
      nonce: await sha256Hex(nonce),
      state,
      prompt: 'select_account',
    }).toString();
    save({ provider, mode, nonce, state, at: Date.now() });
    location.assign(url.toString());
    return;
  }
  const { authClient } = await import('./online');
  const auth = authClient()?.auth;
  if (!auth) throw new SocialFailure('no supabase');
  const { data } = await auth.getSession();
  const link = data.session?.user.is_anonymous === true;
  save({ provider, mode, link, at: Date.now() });
  const options = { redirectTo: back(provider) };
  const { error } = link ? await auth.linkIdentity({ provider, options }) : await auth.signInWithOAuth({ provider, options });
  if (error) throw new SocialFailure(error.message);
}

/**
 * The provider's answer, if the game was opened by one (/auth/<provider>). Read once at start; the
 * address bar goes back to the game's own.
 */
export function readSocialReturn(): SocialReturn | null {
  const m = /^\/auth\/(google|facebook)\/?$/.exec(location.pathname);
  if (!m) return null;
  const provider = m[1] as SocialProvider;
  const hash = new URLSearchParams(location.hash.slice(1));
  const query = new URLSearchParams(location.search);
  const param = (k: string) => hash.get(k) ?? query.get(k);
  history.replaceState(null, '', '/');
  const pending = take();
  if (!pending || pending.provider !== provider) return null;
  const base = { provider, mode: pending.mode };
  const error = param('error');
  if (provider === 'google') {
    if (error) return { ...base, cancelled: error === 'access_denied', failed: error !== 'access_denied' };
    const idToken = hash.get('id_token');
    if (!idToken || hash.get('state') !== pending.state) return { ...base, failed: true };
    return { ...base, idToken, nonce: pending.nonce };
  }
  if (error) {
    const taken = param('error_code') === 'identity_already_exists' || /already linked/i.test(param('error_description') ?? '');
    if (taken) return { ...base, identityTaken: true };
    return { ...base, cancelled: error === 'access_denied', failed: error !== 'access_denied' };
  }
  const access = hash.get('access_token');
  const refresh = hash.get('refresh_token');
  if (!access || !refresh) return { ...base, failed: true };
  return { ...base, link: pending.link, session: { access_token: access, refresh_token: refresh } };
}

/** The identity is someone's already: sign in to that account instead. */
function taken(err: { code?: string; message: string }): boolean {
  return err.code === 'identity_already_exists' || err.code === 'email_exists' || /already (linked|registered)/i.test(err.message);
}

/** Completes a sign-in that came back: linked to the guest, signed in to an account, or round once more. */
export async function finishSocial(r: SocialReturn): Promise<SocialOutcome> {
  if (r.cancelled) return 'cancelled';
  if (r.failed) throw new SocialFailure(`${r.provider}: the provider said no`);
  const { authClient } = await import('./online');
  const auth = authClient()?.auth;
  if (!auth) throw new SocialFailure('no supabase');

  if (r.provider === 'google') {
    const credentials = { provider: 'google' as const, token: r.idToken!, nonce: r.nonce };
    const { data } = await auth.getSession();
    if (data.session?.user.is_anonymous) {
      const { error } = await auth.linkIdentity(credentials);
      if (!error) return 'linked';
      if (!taken(error)) throw new SocialFailure(`google: ${error.message}`);
    }
    const { error } = await auth.signInWithIdToken(credentials);
    if (error) throw new SocialFailure(`google: ${error.message}`);
    return 'signedIn';
  }

  if (r.identityTaken) {
    save({ provider: 'facebook', mode: r.mode, link: false, at: Date.now() });
    const { error } = await auth.signInWithOAuth({ provider: 'facebook', options: { redirectTo: back('facebook') } });
    if (error) throw new SocialFailure(`facebook: ${error.message}`);
    return 'redirecting';
  }
  const { error } = await auth.setSession(r.session!);
  if (error) throw new SocialFailure(`facebook: ${error.message}`);
  return r.link ? 'linked' : 'signedIn';
}
